import './GamesPlayed.scss';

import { Button, Form, Modal, ModalBody } from 'react-bootstrap';
import { ENTER_KEY_CODE, FIRST_QUESTION_ID } from '../../utils/constants';
import {
  getCandidateGameExperiencesPlayed,
  getGames,
  selectGamesPlayed,
  selectIsFetchingGames,
  selectIsFetchingGamesPlayed,
  selectSearchedGames
} from '../../ducks/entities';
import {
  getGameDisplayString,
  scrollToBannerBottom,
  scrollToTop,
  setTitle,
  sortAlphaBy
} from '../../utils/generic';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

import BigAddButton from '../../components/BigAddButton/BigAddButton';
import { Game } from '../../models/game';
import GameCard from '../../components/GameCard/GameCard';
import { GameExperience } from '../../models/survey';
import GameModal from '../../components/GameModal/GameModal';
import { IGDB } from '../../utils/constants';
import ProfileSteps from '../../components/ProfileSteps/ProfileSteps';
import Select from 'react-select';
import cn from 'classnames';
import iconAdd from '../../assets/images/add_button_mobile.webp';
import iconPS from '../../assets/images/ubisoft-playtest-profile-icon-playstation5-black.webp';
import imageStep from '../../assets/images/ubisoft-playtest-profile-mobile-steps-4-4.webp';
import messages from './messagesGamesPlayed';
import noImage from '../../assets/images/ubisoft-playtest-icon-game-no-picture.webp';
import profilMessage from './messages';
import { selectAuth } from '../../ducks/auth';
import { useDebounce } from '@uidotdev/usehooks';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useToggle } from 'react-use';

const sortByOptions = [
  { value: 'name-ASC', label: 'A - Z' },
  { value: 'name-DESC', label: 'Z - A' }
];

const INIT_NUMBER = 5;

const GamesPlayed = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const gamesPlayed = useSelector(selectGamesPlayed);
  const isFetchingGamesPlayed = useSelector(selectIsFetchingGamesPlayed);
  const [showAddGameModal, toggleAddGameModal] = useToggle(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebounce(searchInput, 300);
  const searchedGames = useSelector(selectSearchedGames);
  const isFetchingGames = useSelector(selectIsFetchingGames);
  const [startFetchingGames, toggleStartFetchingGames] = useToggle(false);
  const [showResult, toggleResult] = useToggle(false);
  const [showGameModal, toggleGameModal] = useToggle(false);
  const [selectedGame, setSelectedGame] = useState<Game>();
  const [selectedCandidateGame, setSelectedCandidateGame] =
    useState<GameExperience>();
  const [sortBy, setSortBy] = useState(0);
  const location = useLocation();

  useEffect(() => {
    setTitle(intl.formatMessage({ ...profilMessage.gamesplayed }));
  }, []);

  useEffect(() => {
    location.state?.prevRoute ? scrollToBannerBottom() : scrollToTop();
  }, [gamesPlayed]);

  useEffect(() => {
    dispatch(getCandidateGameExperiencesPlayed());
  }, [auth.userId]);

  useEffect(() => {
    if (searchInput) {
      toggleStartFetchingGames(true);
      toggleResult(true);
    } else {
      toggleResult(false);
    }
  }, [searchInput]);

  useEffect(() => {
    if (debouncedSearchInput) {
      dispatch(getGames(debouncedSearchInput));
      toggleStartFetchingGames(false);
    }
  }, [debouncedSearchInput]);

  const onClickAdd = () => {
    toggleAddGameModal(true);
  };

  const onClickGame = (game: Game) => {
    toggleAddGameModal(false);
    toggleGameModal(true);
    setSelectedGame(game);
  };

  const resetAll = () => {
    toggleGameModal(false);
    setSelectedCandidateGame(undefined);
    setSelectedGame(undefined);
    setSearchInput('');
    toggleResult(false);
  };

  const onEdit = (id: string) => {
    setSelectedCandidateGame(gamesPlayed?.find((game) => game.id === id));
    toggleGameModal(true);
  };

  const sortCandidateGameExperiences = (games: GameExperience[], key: number) =>
    key === 0
      ? games.sort(sortAlphaBy('game.name'))
      : games.sort(sortAlphaBy('game.name')).reverse();

  return (
    <div className="games-played">
      <Modal
        className="search-games"
        show={showAddGameModal}
        onHide={() => {
          toggleAddGameModal(false);
          toggleResult(false);
        }}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton />
        <ModalBody>
          <div className="description">
            <span>{intl.formatMessage({ ...messages.descriptionSearch })}</span>
          </div>
          <div className="igdb">
            <span
              dangerouslySetInnerHTML={{
                __html: intl.formatMessage({ ...messages.poweredByIGDB })
              }}
            />
          </div>
          <Form.Control
            className={cn('input', showResult ? 'input-result-active' : '')}
            type="search"
            aria-label="Search"
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            autoFocus
          />
          {showResult &&
            (startFetchingGames || isFetchingGames ? (
              <div className="result">
                {[...Array(INIT_NUMBER).keys()].map((i) => (
                  <div
                    key={`games-${i}`}
                    className="loading d-flex align-items-center mb-3"
                  >
                    <div className="image" />
                    <p />
                  </div>
                ))}
              </div>
            ) : searchedGames?.length ? (
              <div className="result-wrapper">
                <div className="result">
                  {searchedGames?.map((game) => {
                    const gameName = game.name
                      ? getGameDisplayString(game)
                      : '';
                    const startIndex = gameName
                      .toUpperCase()
                      .indexOf(searchInput.toUpperCase());
                    const endIndex = startIndex + searchInput.length;

                    return (
                      <div
                        key={game.id}
                        className="d-flex align-items-center mb-3"
                        onClick={() => onClickGame(game)}
                        tabIndex={0}
                        onKeyDown={(e: any) => {
                          if (e.keyCode == ENTER_KEY_CODE) e.target.click();
                        }}
                      >
                        <img
                          alt={gameName}
                          src={`${IGDB.baseURL}${game.imageId}`}
                          onError={(e: any) => {
                            e.target.src = noImage;
                          }}
                        />
                        <p>
                          {gameName.substring(0, startIndex)}
                          <b>{gameName.substring(startIndex, endIndex)}</b>
                          {gameName.substring(endIndex)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="result">
                <p>{intl.formatMessage({ ...messages.noGamesFound })}</p>
              </div>
            ))}
        </ModalBody>
      </Modal>
      {showGameModal && (
        <GameModal
          game={selectedGame}
          gameExperience={selectedCandidateGame}
          onBack={() => {
            resetAll();
            selectedCandidateGame
              ? toggleAddGameModal(false)
              : toggleAddGameModal(true);
          }}
          onClose={resetAll}
          submit={true}
        />
      )}
      <div className="header" id={FIRST_QUESTION_ID}>
        <img src={iconPS} alt="" className="mt-n1" />
        <h3>{intl.formatMessage({ ...messages.headerGamesPlayed })}</h3>
      </div>
      <div className="description">
        <span>
          {intl.formatMessage({ ...messages.descriptionGamesPlayed })}
        </span>
      </div>
      <div className="totalAddedGames">
        <span
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ ...messages.totalAddedGames })
          }}
        />
        <span className="number">{gamesPlayed?.length}</span>
      </div>
      <div className="d-flex align-items-center">
        <Select
          className="sortBy"
          classNamePrefix="nemo-select"
          isMulti={false}
          isSearchable={false}
          options={sortByOptions}
          value={sortByOptions[sortBy]}
          onChange={(value: any) => {
            setSortBy(sortByOptions.findIndex((o) => o === value));
          }}
        />
        <Button className="addGameText" variant="primary" onClick={onClickAdd}>
          + {intl.formatMessage({ ...messages.addGame })}
        </Button>
        <img className="addGameImg" src={iconAdd} alt="" onClick={onClickAdd} />
      </div>
      <div className="igdb">
        <span
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage({ ...messages.poweredByIGDB })
          }}
        />
      </div>
      <div className="grid">
        {!isFetchingGamesPlayed && gamesPlayed ? (
          <>
            {gamesPlayed.length < INIT_NUMBER ? (
              <>
                {sortCandidateGameExperiences(gamesPlayed, sortBy).map(
                  (game) => (
                    <GameCard
                      key={game.id}
                      gameExperience={game}
                      onEdit={onEdit}
                    />
                  )
                )}
                {[...Array(INIT_NUMBER - gamesPlayed.length).keys()].map(
                  (i) => (
                    <BigAddButton key={i} onClick={onClickAdd} />
                  )
                )}
              </>
            ) : (
              <>
                {sortCandidateGameExperiences(gamesPlayed, sortBy).map(
                  (game) => (
                    <GameCard
                      key={game.id}
                      gameExperience={game}
                      onEdit={onEdit}
                    />
                  )
                )}
                <BigAddButton onClick={onClickAdd} />
              </>
            )}
          </>
        ) : (
          <>
            {[...Array(INIT_NUMBER).keys()].map((i) => (
              <div key={`gamesPlayed-${i}`} className="loading card">
                <div className="d-flex align-items-center">
                  <div className="image"></div>
                  <div className="ms-3 w-50">
                    <p />
                  </div>
                </div>
                <div className="m-4">
                  {[...Array(5).keys()].map((i) => (
                    <p key={`card-${i}`} />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      <ProfileSteps step={4} steps={4} image={imageStep} />
    </div>
  );
};

export default GamesPlayed;
